import { useCallback, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  hasTableStateInSearch,
  paginationFromTableSearch,
  searchFromPagination
} from "utils/helpers/tableUrlSync";

/**
 * Keeps list table pagination/filters in sync with URL query params.
 * URL is the source of truth on initial load; subsequent interactions update the URL first.
 *
 * Initial load / return-from-detail is owned by useTableViewHeader selectView via
 * mergePaginationFromUrl. Fetching again from the search effect raced that merge and
 * produced extra list requests (often unfiltered, then filtered).
 *
 * @param {((pagination: object) => void)|null} onPaginationFromUrl - refetch when URL changes while the list stays mounted (back/forward)
 */
function useTableUrlSync(onPaginationFromUrl = null) {
  const history = useHistory();
  const { pathname, search } = useLocation();
  const hasUrlTableState = hasTableStateInSearch(search);
  const urlMergeConsumedRef = useRef(false);
  const skipPopHandlerRef = useRef(false);

  useEffect(() => {
    urlMergeConsumedRef.current = false;
  }, [pathname, search]);

  const mergePaginationFromUrl = useCallback(
    (pagination) => {
      if (!hasUrlTableState || urlMergeConsumedRef.current) {
        return pagination;
      }
      urlMergeConsumedRef.current = true;
      return paginationFromTableSearch(search, pagination);
    },
    [hasUrlTableState, search]
  );

  const syncPaginationToUrl = useCallback(
    (pagination) => {
      const nextSearch = searchFromPagination(pagination, search);
      if (nextSearch !== search) {
        skipPopHandlerRef.current = true;
        history.replace({ pathname, search: nextSearch });
      }
    },
    [history, pathname, search]
  );

  useEffect(() => {
    if (!onPaginationFromUrl) {
      return undefined;
    }

    const unlisten = history.listen((location, action) => {
      if (skipPopHandlerRef.current) {
        skipPopHandlerRef.current = false;
        return;
      }
      if (location.pathname !== pathname) {
        return;
      }
      if (!hasTableStateInSearch(location.search)) {
        return;
      }
      if (action === "POP" || action === "PUSH" || action === "REPLACE") {
        onPaginationFromUrl(paginationFromTableSearch(location.search, {}));
      }
    });

    return unlisten;
  }, [history, onPaginationFromUrl, pathname]);

  return {
    hasUrlTableState,
    mergePaginationFromUrl,
    syncPaginationToUrl
  };
}

export default useTableUrlSync;
